import { hasAdvisorAccess } from '@/composables/useRoles';
import { identifyUser, resetAnalytics } from '@/plugins/analytics';

// Sesión de la app en el navegador: lo que guarda un login y lo que borra un
// logout. Un solo lugar para el login normal (Login.vue) y el de demo
// (useDemoAccess): los dos tienen que dejar exactamente lo mismo.

// Claves de localStorage que pertenecen a la sesión de un usuario.
export const APP_SESSION_KEYS = [
  'gs-tkn', 'gs-user', 'gs-company', 'gs-treament',
  'gs-selected-company', 'gs-child-companies', 'gs-master-company',
];

/** Borra la sesión del usuario (sin tocar preferencias como el idioma). */
export const clearAppSession = () => {
  APP_SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
  // Plan elegido en esta pestaña: si no, el siguiente usuario hereda el planId.
  sessionStorage.removeItem('gme.actionPlan.currentPlanId');
  // Perfil demo con el que se entró: sin sesión ya no se "es" ese perfil, así
  // que el selector no lo marca como actual. La sesión de demo (token) queda,
  // para elegir otro perfil sin volver a escribir la contraseña. (Clave de
  // useDemoAccess; se borra acá a mano para no importar ese módulo en círculo.)
  sessionStorage.removeItem('gme.demo.persona');
  // Analítica: lo que pase después ya no es de este usuario.
  resetAnalytics();
};

/**
 * Cierra la sesión en la API (sesión única: docs/single-session-fe-contract.md
 * §2). Sin esto, al entrar desde otro equipo saldría el aviso de "sesión
 * activa". Va ANTES de `clearAppSession`, que borra el token.
 *
 * Devuelve una promesa para que el botón muestre el loading mientras la API
 * responde. Nunca falla y no espera más de `timeoutMs`: la API es idempotente
 * y, si no contesta, el logout local sigue igual (keepalive deja el request
 * en vuelo aunque la página recargue).
 */
export const endServerSession = ({ timeoutMs = 5000 } = {}) => {
  const token = localStorage.getItem('gs-tkn');
  if (!token) return Promise.resolve();
  try {
    const req = fetch(`${process.env.VUE_APP_API}/api/auth/logout`, {
      method: 'POST', keepalive: true, headers: { 'x-token': token },
    }).catch(() => {});
    const timeout = new Promise((resolve) => { setTimeout(resolve, timeoutMs); });
    return Promise.race([req, timeout]);
  } catch {
    return Promise.resolve(); // sin fetch
  }
};

/** Guarda la respuesta de un login (`{ token, user, company, conf }`). */
export const saveAppSession = ({ token, user, company, conf }) => {
  localStorage.setItem('gs-user', JSON.stringify(user));
  if (company != null) localStorage.setItem('gs-company', JSON.stringify(company));
  localStorage.setItem('gs-tkn', token);
  if (conf?.waterTreatment != null) localStorage.setItem('gs-treament', conf.waterTreatment);
  identifyUser(user, company);
};

/** Dashboard de inicio según el tipo de usuario (grupo, naviera, advisor/auditor, empresa). */
export const homeRouteFor = (user = {}) => {
  if (user.isMaster) return 'MasterDashboardRoute';
  if (user.isNetworkOwner) return 'ShippingDashboardRoute';
  if (hasAdvisorAccess(user.rl)) return 'AdvisorCenterRoute';
  return 'HomeRoute';
};
